"use client";

import { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import { Float, Sphere, MeshDistortMaterial, Stars, PerspectiveCamera } from "@react-three/drei";
import * as THREE from "three";
import ConditionalCanvas from "./ConditionalCanvas";

const BRAND_NODES = [
  { color: "#0066ff", position: [-4, 2, 0], speed: 1.5, factor: 0.4 }, // TECH
  { color: "#00f3ff", position: [4, 1.5, -2], speed: 2, factor: 0.5 },  // MODNIX
  { color: "#bd00ff", position: [-3, -1.5, 1], speed: 1.8, factor: 0.3 }, // MEDIA
  { color: "#00ff88", position: [3, -2, 0], speed: 1.2, factor: 0.4 },  // DIY
  { color: "#00aaff", position: [0, 0, -3], speed: 1.4, factor: 0.6 },  // CLOUD
];

function BrandNode({ color, position, speed, factor }: any) {
  const meshRef = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    if (!meshRef.current) return;
    const t = state.clock.getElapsedTime();
    meshRef.current.rotation.x = Math.sin(t / 4);
    meshRef.current.rotation.y = Math.sin(t / 2);
  });

  return (
    <Float speed={speed} rotationIntensity={1} floatIntensity={2}>
      <Sphere args={[0.8, 64, 64]} position={position} ref={meshRef}>
        <MeshDistortMaterial
          color={color}
          speed={speed}
          distort={factor}
          radius={1}
          emissive={color}
          emissiveIntensity={0.5}
          transparent
          opacity={0.8}
        />
      </Sphere>
    </Float>
  );
}

function ConnectionLines() {
  const linesRef = useRef<THREE.Group>(null);
  
  const points = useMemo(() => {
    const p = [];
    for (let i = 0; i < BRAND_NODES.length; i++) {
      for (let j = i + 1; j < BRAND_NODES.length; j++) {
        p.push(new THREE.Vector3(...BRAND_NODES[i].position));
        p.push(new THREE.Vector3(...BRAND_NODES[j].position));
      }
    }
    return p;
  }, []);

  return (
    <group ref={linesRef}>
      {Array.from({ length: points.length / 2 }).map((_, i) => (
        <line key={i}>
          <bufferGeometry attach="geometry">
            <bufferAttribute
              attach="attributes-position"
              args={[
                new Float32Array([
                  points[i * 2].x, points[i * 2].y, points[i * 2].z,
                  points[i * 2 + 1].x, points[i * 2 + 1].y, points[i * 2 + 1].z,
                ]),
                3
              ]}
            />
          </bufferGeometry>
          <lineBasicMaterial attach="material" color="#00f3ff" transparent opacity={0.1} />
        </line>
      ))}
    </group>
  );
}

export default function Brands3DCanvas() {
  return (
    <div className="absolute inset-0 z-0 opacity-40 pointer-events-none">
      <ConditionalCanvas dpr={[1, 2]}>
        <PerspectiveCamera makeDefault position={[0, 0, 10]} fov={50} />
        <ambientLight intensity={0.5} />
        <pointLight position={[10, 10, 10]} intensity={1} color="#00f3ff" />
        <pointLight position={[-10, -10, -10]} intensity={0.5} color="#0066ff" />
        
        <Stars radius={100} depth={50} count={2000} factor={4} saturation={0} fade speed={1} />
        
        <group>
          {BRAND_NODES.map((node, i) => (
            <BrandNode key={i} {...node} />
          ))}
          <ConnectionLines />
        </group>
        
        <fog attach="fog" args={["#040d21", 5, 20]} />
      </ConditionalCanvas>
    </div>
  );
}
